/**
 * SidebarItem — NavLink with icon, label and optional badges.
 */

import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import type { NavItem } from './navigation';

export interface BadgeValues {
  inboxNew?: number;
  inboxMine?: number;
  berichtenUnread?: number;
  webshopPending?: number;
  kasAchterstand?: number;
  risicosOpen?: number;
  verlofPending?: number;
  declaratiesOpen?: number;
  klachtenOpen?: number;
  reparatiesToplannen?: number;
  ophaalActieVereist?: number;
  opvolgenOpen?: number;
  signalenOpen?: number;
  socialWachtrij?: number;
  routeVoorstellen?: number;
}

interface SidebarItemProps {
  item: NavItem;
  badges: BadgeValues;
}

function CountBadge({
  value,
  className,
  title,
}: {
  value: number;
  className: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold text-white',
        className
      )}
    >
      {value}
    </span>
  );
}

export function SidebarItem({ item, badges }: SidebarItemProps) {
  const Icon = item.icon;

  return (
    <li>
      <NavLink
        to={item.href}
        end={item.href === '/'}
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
            isActive
              ? 'bg-indigo-500 text-white shadow-md'
              : 'text-slate-300 hover:bg-slate-800 hover:text-white'
          )
        }
      >
        <Icon />
        <span className="flex-1">{item.name}</span>
        {item.id === 'inbox' && (badges.inboxMine ?? 0) > 0 && (
          <CountBadge
            value={badges.inboxMine!}
            className="bg-indigo-500"
            title={`${badges.inboxMine} gesprek${badges.inboxMine !== 1 ? 'ken' : ''} aan jou toegewezen`}
          />
        )}
        {item.id === 'inbox' && (badges.inboxNew ?? 0) > 0 && (
          <CountBadge value={badges.inboxNew!} className="bg-red-500" />
        )}
        {item.id === 'social' && (badges.socialWachtrij ?? 0) > 0 && (
          <CountBadge
            value={badges.socialWachtrij!}
            className="bg-indigo-500"
            title={`${badges.socialWachtrij} social-voorstel${badges.socialWachtrij !== 1 ? 'len' : ''} wacht${badges.socialWachtrij !== 1 ? 'en' : ''} op goedkeuring`}
          />
        )}
        {item.id === 'routes' && (badges.routeVoorstellen ?? 0) > 0 && (
          <CountBadge
            value={badges.routeVoorstellen!}
            className="bg-amber-500"
            title={`${badges.routeVoorstellen} voorstel${badges.routeVoorstellen !== 1 ? 'len' : ''} voor een extra rit wacht${badges.routeVoorstellen !== 1 ? 'en' : ''} op goedkeuring`}
          />
        )}
        {item.id === 'berichten' && (badges.berichtenUnread ?? 0) > 0 && (
          <CountBadge value={badges.berichtenUnread!} className="bg-indigo-500" />
        )}
        {item.id === 'webshop' && (badges.webshopPending ?? 0) > 0 && (
          <CountBadge value={badges.webshopPending!} className="bg-amber-500" />
        )}
        {item.id === 'kas' && (badges.kasAchterstand ?? 0) > 0 && (
          <CountBadge
            value={badges.kasAchterstand!}
            className="bg-amber-500"
            title={`${badges.kasAchterstand} contante transactie${badges.kasAchterstand !== 1 ? 's' : ''} van vorige dagen nog niet gecontroleerd`}
          />
        )}
        {item.id === 'risicos' && (badges.risicosOpen ?? 0) > 0 && (
          <CountBadge
            value={badges.risicosOpen!}
            className="bg-red-500"
            title={`${badges.risicosOpen} openstaande risico-melding${badges.risicosOpen !== 1 ? 'en' : ''}`}
          />
        )}
        {item.id === 'verlof' && (badges.verlofPending ?? 0) > 0 && (
          <CountBadge value={badges.verlofPending!} className="bg-amber-500" />
        )}
        {item.id === 'declaraties' && (badges.declaratiesOpen ?? 0) > 0 && (
          <CountBadge value={badges.declaratiesOpen!} className="bg-amber-500" />
        )}
        {item.id === 'klachten' && (badges.klachtenOpen ?? 0) > 0 && (
          <CountBadge
            value={badges.klachtenOpen!}
            className="bg-red-600"
            title={`${badges.klachtenOpen} open klacht${badges.klachtenOpen !== 1 ? 'en' : ''}`}
          />
        )}
        {item.id === 'reparaties' && (badges.reparatiesToplannen ?? 0) > 0 && (
          <CountBadge
            value={badges.reparatiesToplannen!}
            className="bg-amber-500"
            title={`${badges.reparatiesToplannen} kleed${badges.reparatiesToplannen !== 1 ? 'en' : ''} bevestigd — wacht op repair-order`}
          />
        )}
        {item.id === 'telefoon' && (badges.opvolgenOpen ?? 0) > 0 && (
          <CountBadge
            value={badges.opvolgenOpen!}
            className="bg-amber-500"
            title={`${badges.opvolgenOpen} terug te bellen — voicemail of gemiste oproep`}
          />
        )}
        {item.id === 'signalen' && (badges.signalenOpen ?? 0) > 0 && (
          <CountBadge
            value={badges.signalenOpen!}
            className="bg-amber-500"
            title={`${badges.signalenOpen} open signa${badges.signalenOpen === 1 ? 'al' : 'len'} uit de systemen`}
          />
        )}
        {item.id === 'orders' && (badges.ophaalActieVereist ?? 0) > 0 && (
          <CountBadge
            value={badges.ophaalActieVereist!}
            className="bg-red-500"
            title={`${badges.ophaalActieVereist} order${badges.ophaalActieVereist !== 1 ? 's' : ''} met ophaal-reminder-actie nodig (bounce of 3+ herinneringen)`}
          />
        )}
      </NavLink>
    </li>
  );
}
